// ViewLending.jsx
import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Divider,
  TextField,
  MenuItem,
  InputAdornment,
  useTheme,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { tokens } from "../../theme";

/* ---------- helpers ---------- */
const arr = (x) => (Array.isArray(x) ? x : []);

// Extracts "YYYY-MM-DD HH:mm" without local timezone shift
const fmtUtcDateTime = (iso) => {
  if (!iso) return "-";
  const [date, time] = iso.split("T");
  return `${date} ${time.slice(0, 5)}`; // "2025-12-12 14:30"
};

// For datetime-local input (NO timezone shift)
const toDatetimeInputUTC = (iso) => {
  if (!iso) return "";
  return iso.slice(0, 16); // "YYYY-MM-DDTHH:mm"
};

// Convert datetime-local → proper UTC ISO string
const fromDatetimeLocalToUTC = (localValue) => {
  if (!localValue) return "";
  return new Date(localValue + ":00Z").toISOString();
};

const fmtDateTime = fmtUtcDateTime;

// "YYYY-MM-DDTHH:mm" from ISO (for <input type="datetime-local">)
const toDatetimeInput = toDatetimeInputUTC;

// turn "YYYY-MM-DDTHH:mm" into ISO; if invalid, return ""
const fromDatetimeInputToISO = fromDatetimeLocalToUTC;

// normalize option arrays to {id, name}
const normalizeList = (arrLike, nameKeys = ["name", "fullName", "title"]) => {
  const a = Array.isArray(arrLike) ? arrLike : [];
  return a
    .map((o) => {
      const id =
        o?.id ??
        o?.ID ??
        o?.Id ??
        o?.userId ??
        o?.customerId ??
        o?.itemId ??
        o?.warehouseId ??
        o?.uuid;
      const key = nameKeys.find((k) => o && o[k] != null);
      const name = o?.[key] ?? o?.label ?? o?.code ?? `#${id ?? "?"}`;
      return id == null ? null : { id, name };
    })
    .filter(Boolean)
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
};

/* ---------- API ---------- */
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    "Cache-Control": "no-cache",
    Pragma: "no-cache",
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, {
      params: { _t: Date.now(), ...(params || {}) },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}
async function putJSON(path, body) {
  try {
    const res = await api.put(path, body, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}
async function deleteJSON(path) {
  try {
    const res = await api.delete(path, {
      headers: { "Cache-Control": "no-cache" },
    });
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message;
    return { data: null, error: msg };
  }
}

/* ---------- page ---------- */
export default function ViewLending() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const lsUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();
  const roleRaw = String(
    lsUser?.role || lsUser?.userRole || lsUser?.roleName || lsUser?.type || ""
  )
    .toLowerCase()
    .trim();

  const isSalesMan =
    /(sales\s*-?\s*man|salesman|sales\s*person|salesperson|^sales$)/i.test(
      roleRaw
    );
  const currentUserId = String(
    lsUser?.id ?? lsUser?.userId ?? lsUser?._id ?? ""
  );
  // table data & ui
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  // dialogs
  const [detailRow, setDetailRow] = React.useState(null);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editValues, setEditValues] = React.useState({
    userId: "",
    customerId: "",
    itemId: "",
    quantity: "",
    warehouseId: "",
    lendingDate: "", // datetime-local
  });
  const [saving, setSaving] = React.useState(false);

  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);

  // options
  const [users, setUsers] = React.useState([]);
  const [customers, setCustomers] = React.useState([]);
  const [items, setItems] = React.useState([]);
  const [warehouses, setWarehouses] = React.useState([]);
  const [optsLoading, setOptsLoading] = React.useState(false);

  // quick lookup maps (for table rendering)
  const usersMap = React.useMemo(
    () => Object.fromEntries(users.map((u) => [Number(u.id), u.name])),
    [users]
  );
  const customersMap = React.useMemo(
    () => Object.fromEntries(customers.map((c) => [Number(c.id), c.name])),
    [customers]
  );
  const itemsMap = React.useMemo(
    () => Object.fromEntries(items.map((i) => [Number(i.id), i.name])),
    [items]
  );
  const warehousesMap = React.useMemo(
    () => Object.fromEntries(warehouses.map((w) => [Number(w.id), w.name])),
    [warehouses]
  );

  // fetch list (cache-busted)
  const load = React.useCallback(async () => {
    setLoading(true);

    // Build API URL based on user role
    let apiUrl = "/api/lending";
    if (isSalesMan) {
      // Sales personnel can only see their own records - use the correct API endpoint
      apiUrl = `/api/lending/user/${currentUserId}`;
    }
    // For non-sales users, use the default URL to get all records

    const { data, error } = await getJSON(apiUrl);
    setLoading(false);
    if (error) {
      setToast({ open: true, msg: "Failed to load sales", sev: "error" });
      return;
    }
    const mapped = arr(data).map((r) => ({
      ...r,
      userName: r?.User?.fullName ?? null,
      customerName: r?.Customer?.name ?? null,
      itemName: r?.Item?.name ?? null,
    }));
    setRows(mapped);
  }, [isSalesMan, currentUserId]); // Add dependencies

  React.useEffect(() => {
    load();
  }, [load]);

  // fetch dropdown options (cache-busted)
  const loadOptions = React.useCallback(async () => {
    setOptsLoading(true);
    try {
      const [userRes, custRes, itemRes, whRes] = await Promise.all([
        getJSON("/api/user"),
        getJSON("/api/customer"),
        getJSON("/api/item"),
        getJSON("/api/warehouse"),
      ]);

      // ---------- Users: filter only sales ----------
      const usersRaw = arr(userRes?.data).filter(
        (u) => String(u?.Role?.name || "").toLowerCase() === "sales"
      );
      const uOpts = normalizeList(usersRaw, ["fullName", "name", "title"]);

      // ---------- Customers: filter only type customer ----------
      const isCustomer = (c) => {
        const v = (c?.type ?? c?.customerType ?? c?.role ?? "")
          .toString()
          .toLowerCase();
        return v === "customer" || v === "customers";
      };
      const customersRaw = arr(custRes?.data).filter(isCustomer);
      const cOpts = normalizeList(customersRaw, ["name"]);

      // ---------- Items & Warehouses ----------
      const iOpts = normalizeList(itemRes?.data, ["name"]);
      const wOpts = normalizeList(whRes?.data, ["name"]);

      setUsers(uOpts);
      setCustomers(cOpts);
      setItems(iOpts);
      setWarehouses(wOpts);
    } catch (err) {
      setToast({ open: true, msg: "Failed to load dropdowns", sev: "error" });
    } finally {
      setOptsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  // update (optimistic + refetch)
  const handleUpdate = async () => {
    if (!editRow?.id) return;

    const qty = Number(editValues.quantity);
    if (Number.isNaN(qty) || qty < 1) {
      setToast({
        open: true,
        msg: "Quantity must be a number ≥ 1",
        sev: "error",
      });
      return;
    }

    // Accept datetime-local -> ISO (backend sample accepts "2025-10-13T15:00:00Z")
    const lendingISO = fromDatetimeInputToISO(editValues.lendingDate);
    if (!lendingISO) {
      setToast({
        open: true,
        msg: "Please provide a valid lending date & time",
        sev: "error",
      });
      return;
    }

    const payload = {
      userId: Number(editValues.userId),
      customerId: Number(editValues.customerId),
      itemId: Number(editValues.itemId),
      quantity: qty,
      warehouseId: Number(editValues.warehouseId),
      lendingDate: lendingISO,
    };

    setSaving(true);

    // optimistic merge
    const prevRows = [...rows];
    const updatedOptimistic = {
      ...editRow,
      ...payload,
      userName:
        usersMap[payload.userId] ?? editRow.userName ?? `#${payload.userId}`,
      customerName:
        customersMap[payload.customerId] ??
        editRow.customerName ??
        `#${payload.customerId}`,
      itemName:
        itemsMap[payload.itemId] ?? editRow.itemName ?? `#${payload.itemId}`,
      lendingDate: lendingISO,
      // table uses map to render warehouse; keeping ID is enough
    };
    setRows((prev) =>
      prev.map((r) => (r.id === editRow.id ? updatedOptimistic : r))
    );

    const { error } = await putJSON(`/api/lending/${editRow.id}`, payload);
    setSaving(false);

    if (error) {
      // rollback on error
      setRows(prevRows);
      setToast({ open: true, msg: "Update failed", sev: "error" });
      return;
    }

    setToast({
      open: true,
      msg: "Lending updated successfully",
      sev: "success",
    });
    setEditOpen(false);
    setEditRow(null);

    // refresh for canonical data
    await load();
  };

  // delete (optimistic + refetch + toast)
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const prevRows = [...rows];
    setRows((rs) => rs.filter((r) => r.id !== deleteRow.id));

    const { data, error } = await deleteJSON(`/api/lending/${deleteRow.id}`);
    setDeleting(false);

    if (error) {
      setRows(prevRows);
      setToast({ open: true, msg: "Delete failed", sev: "error" });
      return;
    }

    setToast({
      open: true,
      msg: data?.message || "Lending deleted successfully",
      sev: "success",
    });
    setDeleteRow(null);
    await load();
  };

  // columns to display in table (warehouse now uses warehousesMap)
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) => {
        // index among visible rows on the current page
        const visibleIndex = params.api.getRowIndexRelativeToVisibleRows(
          params.id
        );
        // page & pageSize for absolute numbering across pages
        const pm = params.api?.state?.pagination?.paginationModel || {
          page: 0,
          pageSize: rows.length || 0,
        };
        const pageOffset = (pm.page || 0) * (pm.pageSize || 0);
        return pageOffset + visibleIndex + 1;
      },
    },
    { field: "id", headerName: "ID", width: 80, hide: true },
    {
      field: "item",
      headerName: "Item",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) =>
        params.row.itemName ??
        itemsMap[Number(params.row.itemId)] ??
        `#${params.row.itemId}`,
    },
    {
      field: "customer",
      headerName: "Customer",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) =>
        params.row.customerName ??
        customersMap[Number(params.row.customerId)] ??
        `#${params.row.customerId}`,
    },
    {
      field: "user",
      headerName: "User",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) =>
        params.row.userName ??
        usersMap[Number(params.row.userId)] ??
        `#${params.row.userId}`,
    },
    {
      field: "warehouse",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) =>
        warehousesMap[Number(params.row.warehouseId)] ??
        `#${params.row.warehouseId}`,
    },
    {
      field: "quantity",
      headerName: "Quantity",
      width: 110,
      type: "number",
      headerAlign: "left",
      align: "left",
      valueGetter: (p) => p.row.quantity,
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 170,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const row = params.row;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Details">
              <IconButton size="small" onClick={() => setDetailRow(row)}>
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  setEditRow(row);
                  setEditValues({
                    userId: Number(row.userId) || "",
                    customerId: Number(row.customerId) || "",
                    itemId: Number(row.itemId) || "",
                    quantity: Number(row.quantity ?? "") || "",
                    warehouseId: Number(row.warehouseId) || "",
                    lendingDate: toDatetimeInput(row.lendingDate),
                  });
                  setEditOpen(true);
                }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // grid look (theme-consistent)
  const colorsTheme = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": {
      backgroundColor: colors.primary[400],
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": {
      color: `${colors.greenAccent[200]} !important`,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  return (
    <>
      <Box m="20px">
        <Header
          title="LENDING"
          subtitle="View, edit, and manage lending records"
        />

        <Box m="40px 0 0 0">
          <SimpleTable
            rows={rows}
            columns={columns}
            loading={loading}
            height="75vh"
            getRowId={(r) => r.id}
            sx={colorsTheme}
          />
        </Box>
      </Box>

      {/* Details */}
      <Dialog
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Lending Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Item"
                  value={
                    detailRow.itemName ??
                    itemsMap[Number(detailRow.itemId)] ??
                    `#${detailRow.itemId}`
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Customer"
                  value={
                    detailRow.customerName ??
                    customersMap[Number(detailRow.customerId)] ??
                    `#${detailRow.customerId}`
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="User"
                  value={
                    detailRow.userName ??
                    usersMap[Number(detailRow.userId)] ??
                    `#${detailRow.userId}`
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Warehouse"
                  value={
                    warehousesMap[Number(detailRow.warehouseId)] ??
                    `#${detailRow.warehouseId}`
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <LabelValue label="Quantity" value={detailRow.quantity} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <LabelValue
                  label="Lending Date"
                  value={fmtDateTime(detailRow.lendingDate)}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Lending</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            {/* User field - Hidden for salesman */}
            {!isSalesMan && (
              <TextField
                label="User"
                select
                value={editValues.userId}
                onChange={(e) =>
                  setEditValues((s) => ({
                    ...s,
                    userId: e.target.value === "" ? "" : Number(e.target.value),
                  }))
                }
                fullWidth
                variant="filled"
                disabled={optsLoading}
                helperText={optsLoading ? "Loading users..." : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Refresh users">
                        <span>
                          <IconButton
                            size="small"
                            onClick={loadOptions}
                            disabled={optsLoading}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={Number(u.id)}>
                    {u.name ?? `#${u.id}`}
                  </MenuItem>
                ))}
              </TextField>
            )}

            {/* Hidden field for salesmen to maintain userId */}
            {isSalesMan && <input type="hidden" value={editValues.userId} />}

            {/* Customer dropdown + refresh */}
            <TextField
              label="Customer"
              select
              value={editValues.customerId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  customerId:
                    e.target.value === "" ? "" : Number(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              helperText={optsLoading ? "Loading customers..." : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh customers">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={Number(c.id)}>
                  {c.name ?? `#${c.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Item dropdown + refresh */}
            <TextField
              label="Item"
              select
              value={editValues.itemId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  itemId: e.target.value === "" ? "" : Number(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              helperText={optsLoading ? "Loading items..." : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh items">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {items.map((it) => (
                <MenuItem key={it.id} value={Number(it.id)}>
                  {it.name ?? `#${it.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Warehouse dropdown + refresh */}
            <TextField
              label="Warehouse"
              select
              value={editValues.warehouseId}
              onChange={(e) =>
                setEditValues((s) => ({
                  ...s,
                  warehouseId:
                    e.target.value === "" ? "" : Number(e.target.value),
                }))
              }
              fullWidth
              variant="filled"
              disabled={optsLoading}
              helperText={optsLoading ? "Loading warehouses..." : ""}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Refresh warehouses">
                      <span>
                        <IconButton
                          size="small"
                          onClick={loadOptions}
                          disabled={optsLoading}
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={Number(w.id)}>
                  {w.name ?? `#${w.id}`}
                </MenuItem>
              ))}
            </TextField>

            {/* Quantity */}
            <TextField
              label="Quantity"
              type="number"
              value={editValues.quantity}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, quantity: e.target.value }))
              }
              fullWidth
              variant="filled"
              inputProps={{ min: 1, style: { textAlign: "left" } }}
              required
            />

            {/* Lending Date/Time */}
            <TextField
              label="Lending Date & Time"
              type="datetime-local"
              value={editValues.lendingDate}
              onChange={(e) =>
                setEditValues((s) => ({ ...s, lendingDate: e.target.value }))
              }
              fullWidth
              variant="filled"
              InputLabelProps={{ shrink: true }}
              helperText="Pick date and time"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            variant="contained"
            color="secondary"
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Lending</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete it?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ---------- subcomponents ---------- */
function LabelValue({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {value ?? "-"}
      </Typography>
      <Divider sx={{ mt: 1 }} />
    </Box>
  );
}
